import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setToken } from '../utils/auth';

const SSOCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const ssoError = searchParams.get('sso_error');

    if (ssoError) {
      const errorMessages = {
        validation_failed: 'SSO authentication failed. Please try again or contact your administrator.',
        user_not_found: 'No account found for your SSO identity. Contact your administrator to provision your account.',
        account_inactive: 'Your account has been deactivated. Contact your administrator.',
      };
      setError(errorMessages[ssoError as keyof typeof errorMessages] || 'SSO authentication failed.');
      return;
    }

    if (!token) {
      setError('No authentication token received.');
      return;
    }

    // Store token and refresh auth context
    setToken(token);
    refreshAuth().then(() => {
      navigate('/', { replace: true });
    }).catch(() => {
      setError('Failed to complete SSO login. Please try again.');
    });
  }, [searchParams, navigate, refreshAuth]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                SSO Login Failed
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {error}
              </p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center items-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Completing SSO login...</p>
    </div>
  );
};

export default SSOCallbackPage;
